import { createSlice, createAsyncThunk, createSelector } from "@reduxjs/toolkit";
import { AxiosInstance } from "axios";
import dayjs from "dayjs";
import { IEvent } from "../interfaces/IEvent";
import { IEventList } from "../interfaces/IEventList";
import { EventService } from "../services/eventService";
import { AppDispatch, RootState } from "./store";
import { IEventDay } from "../interfaces/IEventDay";
import { EventStatus } from "../enums/EventStatus";
import { EventType } from "../enums/EventType";
import { EventDayService } from "../services/eventDayService";
import { IEventSession } from "../interfaces/IEventSession";
import { EventSessionService } from "../services/eventSessionService";
import { SessionDelegateService } from "../services/sessionDelegateService";
import { ISessionDelegate } from "../interfaces/ISessionDelegate";
import { ITicket } from "../interfaces/ITicket";
import { fetchTicketsByEvent } from "./ticketsSlice";
import { IAssignTicket } from "../interfaces/IAssignTicket";
import { IPagedFilterResponse } from "../interfaces/IPagedFilterResponse";
import { AttendeeVisibility } from "../enums/AttendeeVisibility";
import { EventRegistrationStatus } from "../enums/EventRegistrationStatus";
import { IQuickEvent } from "../interfaces/IQuickEvent";

const ATTENDEE_ROLE_ID = 'd3e3f3a3-3b3c-4d3e-9f3a-3b3c3d3e3f3a';

// Selectors
export const selectEventsLoading = (state: RootState) => state.events.loadingEvent;
export const selectDaysLoading = (state: RootState) => state.events.loadingDays;
export const selectEvents = (state: RootState) => state.events.events;
export const selectFullEventById = (state: RootState, id: string) =>
    state.events.fullEvents.find(evt => evt.id === id) || null;

export const selectTicketAssignmentsByEventId = (state: RootState, eventId: string) => state.events.ticketAssignments[eventId] || [];
export const selectTicketAssignmentsLoading = (state: RootState) => state.events.loadingTicketAssignments;
export const getEventAttendees = createSelector(
    [
        (state: RootState) => state.events.fullEvents,
        (_: RootState, eventId: string) => eventId
    ],
    (fullEvents, eventId) => {
        const event = fullEvents.find(evt => evt.id === eventId);
        if (!event) return [];
        const delegates: ISessionDelegate[] = [];
        for (const day of event.eventDays ?? []) {
            for (const session of day.eventSessions ?? []) {
                for (const delegate of session.sessionDelegates ?? []) {
                    if (delegate.roleId === ATTENDEE_ROLE_ID) {
                        delegates.push(delegate);
                    }
                }
            }
        }
        return delegates;
    }
);
export const getEventNonAttendees = createSelector(
    [
        (state: RootState) => state.events.fullEvents,
        (_: RootState, eventId: string) => eventId
    ],
    (fullEvents, eventId) => {
        const event = fullEvents.find(evt => evt.id === eventId);
        if (!event) return [];
        const delegates: ISessionDelegate[] = [];
        for (const day of event.eventDays ?? []) {
            for (const session of day.eventSessions ?? []) {
                for (const delegate of session.sessionDelegates ?? []) {
                    if (delegate.roleId !== ATTENDEE_ROLE_ID) {
                        delegates.push(delegate);
                    }
                }
            }
        }
        return delegates;
    }
);
export const getEventSessions = createSelector(
    [
        (state: RootState) => state.events.fullEvents,
        (_: RootState, eventId: string) => eventId,
    ],
    (fullEvents, eventId) => {
        const event = fullEvents.find(evt => evt.id === eventId);
        if (!event) return [];
        const sessions: IEventSession[] = [];
        for (const day of event.eventDays ?? []) {
            for (const session of day.eventSessions ?? []) {
                sessions.push(session);
            }
        }
        return sessions;
    }
);
export const getAllEventSessionDelegates = createSelector(
    [
        (state: RootState) => state.events.fullEvents,
        (_: RootState, eventId: string) => eventId
    ],
    (fullEvents, eventId) => {
        const event = fullEvents.find(evt => evt.id === eventId);
        if (!event) return [];
        const delegates: ISessionDelegate[] = [];
        for (const day of event.eventDays ?? []) {
            for (const session of day.eventSessions ?? []) {
                for (const delegate of session.sessionDelegates ?? []) {
                    if (delegate.roleId !== ATTENDEE_ROLE_ID) {
                        delegates.push(delegate);
                    }
                }
            }
        }
        return delegates;
    }
);



// Async actions
export const fetchEvents = createAsyncThunk<
    { data: IEventList[]; status?: string | number; totalCount: number },
    {
        apiClient: AxiosInstance;
        status?: string | number;
        page?: number;
        pageSize?: number;
        sortBy?: string;
        sortDesc?: boolean;
        filters?: Record<string, string>;
    }
>(
    'events/fetch',
    async ({
        apiClient,
        status,
        page = 1,
        pageSize = 10,
        sortBy = 'Title',
        sortDesc = false,
        filters = {},
    }) => {
        const params = new URLSearchParams();

        const statusMap: Record<string, number> = {
            draft: 0,
            schedule: 2,
            inProgress: 3,
            cancelled: 5,
            completed: 6,
        };

        // Normalize status: if it’s a named string, convert to number. If it’s 'all', omit it.
        const normalizedStatus =
            typeof status === 'string'
                ? statusMap[status] ?? (status !== 'all' ? status : undefined)
                : status;

        if (normalizedStatus !== undefined) {
            params.append('status', normalizedStatus.toString());
        }

        params.append('page', page.toString());
        params.append('pageSize', pageSize.toString());
        params.append('sortBy', sortBy);
        params.append('sortDesc', sortDesc.toString());

        for (const [key, value] of Object.entries(filters)) {
            if (value) params.append(key, value);
        }

        const response = await apiClient.get<IPagedFilterResponse<IEventList>>(
            `/admin/event/paged?${params.toString()}`
        );

        return {
            data: response.data.items,
            status,
            totalCount: response.data.totalCount,
        };
    }
);


export const fetchAllEvents = createAsyncThunk<IEventList[], { apiClient: AxiosInstance }>(
    'events/fetchAll',
    async ({ apiClient }) => {
        const response = await apiClient.get('/admin/event');
        return response.data;
    }
);


export const fetchEventById = createAsyncThunk<
    IEvent,
    { eventId: string; apiClient: AxiosInstance; force?: boolean },
    { state: RootState }
>(
    "events/fetchById",
    async ({ eventId, apiClient, force }, { getState }) => {
        const { fullEvents, eventFetched } = getState().events;
        const event = fullEvents.find(evt => evt.id === eventId);
        const lastFetched = eventFetched[eventId] || 0;
        const FIVE_MINUTES = 5 * 60 * 1000;

        if (!force && event && Date.now() - lastFetched < FIVE_MINUTES) {
            return event;
        }

        const eventService = new EventService(apiClient);
        return await eventService.get(eventId);
    }
);


export const fetchEventDays = createAsyncThunk<IEventDay[], { eventId: string; apiClient: AxiosInstance; force?: boolean }, { state: RootState }>(
    "events/fetchDays",
    async ({ eventId, apiClient }) => {
        const eventDayService = new EventDayService(apiClient);
        return await eventDayService.getAll(eventId);
    },
    {
        condition: ({ eventId, force }, { getState }) => {
            if (force) return true;
            const { fullEvents, eventDaysFetched } = (getState() as RootState).events;
            const event = fullEvents.find(evt => evt.id === eventId);
            const lastFetched = eventDaysFetched[eventId] || 0;
            const FIVE_MINUTES = 5 * 60 * 1000;
            return !(event?.eventDays?.length && Date.now() - lastFetched < FIVE_MINUTES);
        }
    }
);

export const fetchEventDaySessions = createAsyncThunk<
    { eventDayId: string; sessions: IEventSession[] },
    { eventDayId: string; apiClient: AxiosInstance; force?: boolean },
    { state: RootState }
>(
    "events/fetchEventDaySessions",
    async ({ eventDayId, apiClient }) => {
        const eventSessionService = new EventSessionService(apiClient);
        const sessions = await eventSessionService.getAll(eventDayId);
        return { eventDayId, sessions };
    },
    {
        condition: ({ eventDayId, force }, { getState }) => {
            if (force) return true;
            const { fullEvents, eventDaySessionsFetched } = (getState() as RootState).events;
            const lastFetched = eventDaySessionsFetched[eventDayId] || 0;
            const FIVE_MINUTES = 5 * 60 * 1000;
            let sessions: IEventSession[] | undefined;
            for (const event of fullEvents) {
                const day = event.eventDays?.find(d => d.id === eventDayId);
                if (day?.eventSessions?.length) {
                    sessions = day.eventSessions;
                    break;
                }
            }
            return !(sessions && Date.now() - lastFetched < FIVE_MINUTES);
        }
    }
);

export const fetchSessionDelegates = createAsyncThunk<
    { sessionId: string; delegates: ISessionDelegate[] },
    { sessionId: string; apiClient: AxiosInstance; force?: boolean },
    { state: RootState }
>(
    "events/fetchSessionDelegates",
    async ({ sessionId, apiClient }) => {
        const sessionDelegateService = new SessionDelegateService(apiClient);
        const delegates = await sessionDelegateService.getAll(sessionId);
        return { sessionId, delegates };
    },
    {
        condition: ({ sessionId, force }, { getState }) => {
            if (force) return true;
            const { fullEvents, sessionDelegatesFetched } = (getState() as RootState).events;
            const lastFetched = sessionDelegatesFetched[sessionId] || 0;
            const FIVE_MINUTES = 5 * 60 * 1000;
            let cachedDelegates: ISessionDelegate[] | undefined;
            for (const event of fullEvents) {
                for (const day of event.eventDays ?? []) {
                    const session = day.eventSessions?.find(s => s.id === sessionId);
                    if (session && Array.isArray(session.sessionDelegates) && session.sessionDelegates.length > 0) {
                        cachedDelegates = session.sessionDelegates;
                        break;
                    }
                }
                if (cachedDelegates) break;
            }
            return !(cachedDelegates && Date.now() - lastFetched < FIVE_MINUTES);
        }
    }
);

export const fetchEventTickets = createAsyncThunk<
    { eventId: string; tickets: ITicket[] },
    { eventId: string; apiClient: AxiosInstance, force?: boolean },
    { dispatch: AppDispatch; state: RootState }
>(
    "events/fetchEventTickets",
    async ({ eventId, apiClient }, { dispatch }) => {
        const tickets = await dispatch(fetchTicketsByEvent({ eventId, apiClient })).unwrap();
        return { eventId, tickets };
    },
    {
        condition: ({ eventId, force }, { getState }) => {
            if (force) return true;
            const { fullEvents, eventFetched } = (getState() as RootState).events;
            const event = fullEvents.find(evt => evt.id === eventId);
            const lastFetched = eventFetched[eventId] || 0;
            const FIVE_MINUTES = 5 * 60 * 1000;
            return !(event?.tickets?.length && Date.now() - lastFetched < FIVE_MINUTES);
        }
    }
);

export const fetchTicketAssignments = createAsyncThunk<
    { eventId: string; assignments: IAssignTicket[] },
    { eventId: string; apiClient: AxiosInstance; delegateId?: string },
    { state: RootState }
>(
    "events/fetchTicketAssignments",
    async ({ eventId, apiClient, delegateId }) => {
        const eventService = new EventService(apiClient);
        const assignments = await eventService.getTicketAssignments(eventId, delegateId);
        return { eventId, assignments };
    }
);

export const addEvent = createAsyncThunk<IEvent, { event: IEvent; apiClient: AxiosInstance, image?: File | null }>(
    "events/add",
    async ({ event, apiClient, image }) => {
        const eventService = new EventService(apiClient);
        return await eventService.create(event, image);
    }
);

export const addQuickEvent = createAsyncThunk<IEvent, { event: IQuickEvent; apiClient: AxiosInstance, image?: File | null }>(
    "events/addQuick",
    async ({ event, apiClient, image }) => {
        const eventService = new EventService(apiClient);
        return await eventService.createQuick(event, image);
    }
);

export const updateEvent = createAsyncThunk<IEvent, { eventId: string; updatedEvent: IEvent; apiClient: AxiosInstance, image?: File | null }>(
    "events/update",
    async ({ eventId, updatedEvent, apiClient, image }) => {
        const eventService = new EventService(apiClient);
        return await eventService.update(eventId, updatedEvent, image);
    }
);

export const deleteEvent = createAsyncThunk<string, { eventId: string; apiClient: AxiosInstance }>(
    "events/delete",
    async ({ eventId, apiClient }) => {
        const eventService = new EventService(apiClient);
        await eventService.delete(eventId);
        return eventId;
    }
);

export const cancelEvent = createAsyncThunk<{ eventId: string }, { eventId: string; apiClient: AxiosInstance }
>(
    "events/cancelEvent",
    async ({ eventId, apiClient }) => {
        const eventService = new EventService(apiClient);
        await eventService.cancelEvent(eventId); // You’ll need to implement this in your EventService
        return { eventId };
    }
);

export const assignEventCoordinators = createAsyncThunk<void, { eventId: string; coordinatorIds: string[]; apiClient: AxiosInstance }>(
    "events/assignCoordinators",
    async ({ eventId, coordinatorIds, apiClient }) => {
        const eventService = new EventService(apiClient);
        await eventService.assignCoordinators(eventId, coordinatorIds);
    }
);

export const publishEvent = createAsyncThunk<
    { eventId: string; categoryId: string; subCategoryId: string; notes: string; status: EventStatus },
    { eventId: string; categoryId: string; subCategoryId: string; notes: string, apiClient: AxiosInstance },
    { state: RootState }
>(
    "events/publish",
    async ({ eventId, categoryId, subCategoryId, notes, apiClient }) => {
        const eventService = new EventService(apiClient);
        const result = await eventService.publish(eventId, categoryId, subCategoryId, notes);
        return {
            eventId: result.eventId,
            categoryId: result.categoryId,
            subCategoryId: result.subCategoryId,
            notes: result.notes,
            status: result.status
        };
    }
);

/** Adds a newly created event to both the list and full-event caches. */
const applyCreatedEvent = (
    state: { events: IEventList[]; fullEvents: IEvent[] },
    payload: IEvent
) => {
    state.fullEvents = state.fullEvents.filter(evt => evt.id !== undefined && evt.id !== null);
    const eventListItem: IEventList = {
        id: payload.id ?? "",
        title: payload.title,
        tags: payload.tags,
        registrationDeadline: payload.registrationDeadline,
        isInviteOnly: payload.isInviteOnly,
        description: payload.description,
        startDate: payload.startDate,
        endDate: payload.endDate,
        status: payload.status,
        imageUrl: payload.imageUrl,
        attendees: 0,
        location: "",
        price: 0,
        isWaitlisted: false,
        taskCounts: [],
        coordinators: []
    };
    state.events.push(eventListItem);
    state.fullEvents.push(payload);
};

const eventsSlice = createSlice({
    name: "events",
    initialState: {
        events: [] as IEventList[],
        allEvents: [] as IEventList[],
        fullEvents: [] as IEvent[],
        ticketAssignments: {} as Record<string, IAssignTicket[]>,
        loadingEvent: false,
        totalCount: 0,
        loadingAttendees: false,
        loadingDays: false,
        loadingSessions: false,
        loadingDelegates: false,
        loadingTicketAssignments: false,
        lastFetched: 0,
        eventFetched: {} as Record<string, number>,
        eventDaysFetched: {} as Record<string, number>,
        eventDaySessionsFetched: {} as Record<string, number>,
        sessionDelegatesFetched: {} as Record<string, number>,
    },
    reducers: {
        resetFullEvents: (state) => {
            state.fullEvents = [];
            state.eventFetched = {};
            state.eventDaysFetched = {};
            state.eventDaySessionsFetched = {};
            state.sessionDelegatesFetched = {};
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchEvents.pending, (state) => {
                state.loadingEvent = true;
            })
            .addCase(fetchEvents.fulfilled, (state, action) => {
                state.loadingEvent = false;
                state.events = action.payload.data;
                state.totalCount = action.payload.totalCount;
                state.lastFetched = Date.now();
            })

            .addCase(fetchEvents.rejected, (state) => {
                state.loadingEvent = false; // ✅ Stop loading on error
            })
            .addCase(fetchAllEvents.fulfilled, (state, action) => {
                state.loadingEvent = false;
                state.allEvents = action.payload; // ✅ This is what we want
                state.lastFetched = Date.now();
            })


            .addCase(fetchEventById.pending, (state) => {
                state.loadingEvent = true;
            })
            .addCase(fetchEventById.fulfilled, (state, action) => {
                state.loadingEvent = false;
                const idx = state.fullEvents.findIndex(evt => evt.id === action.payload.id);
                if (idx >= 0) {
                    const existingEvent = state.fullEvents[idx];
                    state.fullEvents[idx] = {
                        ...existingEvent,
                        ...action.payload,
                        eventDays: action.payload.eventDays ?? existingEvent.eventDays,
                        tickets: action.payload.tickets ?? existingEvent.tickets,
                        coordinators: action.payload.coordinators ?? existingEvent.coordinators,
                    };
                } else {
                    state.fullEvents.push(action.payload);
                }
                if (action.payload.id) {
                    state.eventFetched[action.payload.id] = Date.now();
                }
            })
            .addCase(fetchEventDays.pending, (state) => {
                state.loadingDays = true;
            })
            .addCase(fetchEventDays.fulfilled, (state, action) => {
                state.loadingDays = false;
                const { eventId } = action.meta.arg;
                const eventDays = action.payload;
                state.eventDaysFetched[eventId] = Date.now();

                const eventIndex = state.fullEvents.findIndex(evt => evt.id === eventId);
                if (eventIndex !== -1) {
                    state.fullEvents[eventIndex] = {
                        ...state.fullEvents[eventIndex],
                        eventDays
                    };
                }
            })
            .addCase(fetchEventDaySessions.fulfilled, (state, action) => {
                const { eventDayId, sessions } = action.payload;
                state.eventDaySessionsFetched[eventDayId] = Date.now();

                for (const event of state.fullEvents) {
                    const day = event.eventDays?.find(d => d.id === eventDayId);
                    if (day) {
                        day.eventSessions = sessions;
                        break;
                    }
                }
            })
            .addCase(fetchSessionDelegates.fulfilled, (state, action) => {
                const { sessionId, delegates } = action.payload;
                state.sessionDelegatesFetched[sessionId] = Date.now();
                if (delegates !== undefined) {
                    for (const event of state.fullEvents) {
                        for (const day of event.eventDays ?? []) {
                            const session = day.eventSessions?.find(s => s.id === sessionId);
                            if (session) {
                                session.sessionDelegates = delegates;
                                return;
                            }
                        }
                    }
                }
            })
            .addCase(fetchEventTickets.fulfilled, (state, action) => {
                const { eventId, tickets } = action.payload;
                const eventIndex = state.fullEvents.findIndex(evt => evt.id === eventId);
                if (eventIndex !== -1) {
                    state.fullEvents[eventIndex] = {
                        ...state.fullEvents[eventIndex],
                        tickets
                    };
                }
            })
            .addCase(fetchTicketAssignments.pending, (state) => {
                state.loadingTicketAssignments = true;
            })
            .addCase(fetchTicketAssignments.fulfilled, (state, action) => {
                state.loadingTicketAssignments = false;
                state.ticketAssignments[action.payload.eventId] = action.payload.assignments;
            })
            .addCase(fetchTicketAssignments.rejected, (state) => {
                state.loadingTicketAssignments = false;
            })
            .addCase(addEvent.fulfilled, (state, action) => {
                applyCreatedEvent(state, action.payload);
            })
            .addCase(addQuickEvent.fulfilled, (state, action) => {
                applyCreatedEvent(state, action.payload);
            })
            .addCase(updateEvent.fulfilled, (state, action) => {
                const eventListItem: IEventList = {
                    id: action.payload.id ?? "",
                    title: action.payload.title,
                    tags: action.payload.tags,
                    registrationDeadline: action.payload.registrationDeadline,
                    isInviteOnly: action.payload.isInviteOnly,
                    description: action.payload.description,
                    startDate: action.payload.startDate,
                    endDate: action.payload.endDate,
                    status: action.payload.status,
                    imageUrl: (action.payload as any).imageUrl ?? "",
                    attendees: 0,
                    location: "",
                    price: 0,
                    isWaitlisted: false,
                    coordinators: [],
                    taskCounts: []
                };
                state.events = state.events.map(evt =>
                    evt.id === action.payload.id ? eventListItem : evt
                );
                const idx = state.fullEvents.findIndex(evt => evt.id === action.payload.id);
                if (idx >= 0) {
                    const existingEvent = state.fullEvents[idx];
                    state.fullEvents[idx] = {
                        ...existingEvent,
                        ...action.payload,
                        eventDays: action.payload.eventDays ?? existingEvent.eventDays,
                        tickets: action.payload.tickets ?? existingEvent.tickets,
                        coordinators: action.payload.coordinators ?? existingEvent.coordinators,
                    };
                } else {
                    state.fullEvents.push(action.payload);
                }
            })
            .addCase(deleteEvent.fulfilled, (state, action) => {
                state.events = state.events.filter(evt => evt.id !== action.payload);
                state.fullEvents = state.fullEvents.filter(evt => evt.id !== action.payload);
                state.allEvents = state.allEvents.filter(evt => evt.id !== action.payload);
            })
            .addCase(cancelEvent.fulfilled, (state, action) => {
                const { eventId } = action.payload;
                const eventIndex = state.fullEvents.findIndex(evt => evt.id === eventId);
                if (eventIndex !== -1) {
                    state.fullEvents[eventIndex].status = EventStatus.Cancelled;
                }
                const eventListIndex = state.events.findIndex(evt => evt.id === eventId);
                if (eventListIndex !== -1) {
                    state.events[eventListIndex].status = EventStatus.Cancelled;
                }
            })
            .addCase(assignEventCoordinators.fulfilled, (state, action) => {
                const { eventId, coordinatorIds } = action.meta.arg;
                const eventIndex = state.fullEvents.findIndex(evt => evt.id === eventId);
                if (eventIndex !== -1) {
                    state.fullEvents[eventIndex].coordinators = coordinatorIds;
                }
            })
            .addCase(publishEvent.fulfilled, (state, action) => {
                const { eventId, categoryId, subCategoryId, notes, status } = action.payload;

                // Find the event in the store and update only the specified fields
                const eventIndex = state.fullEvents.findIndex(evt => evt.id === eventId);
                if (eventIndex !== -1) {
                    state.fullEvents[eventIndex] = {
                        ...state.fullEvents[eventIndex],
                        categoryId,
                        subCategoryId,
                        //notes,
                        status
                    };
                }

                // Optionally update the `events` list if it reflects the same fields
                const eventListIndex = state.events.findIndex(evt => evt.id === eventId);
                if (eventListIndex !== -1) {
                    state.events[eventListIndex] = {
                        ...state.events[eventListIndex],
                        status // Update only the status in the list view
                    };
                }
            });

    }
});

export const { resetFullEvents } = eventsSlice.actions;
export default eventsSlice.reducer;
